import React from "react";
import {
  BookOpen,
  Receipt,
  Wrench,
  Calendar,
  Users,
  ArrowRightLeft,
  Landmark,
  Sparkles,
  Eraser,
  Wallet,
  LineChart,
} from "lucide-react";

const CORE = [
  {
    Icon: BookOpen,
    title: "Full-Cycle Bookkeeping",
    desc: "Day-to-day transaction coding, journal entries, and a clean general ledger—month after month.",
    span: "md:col-span-1",
    accent: true,
  },
  {
    Icon: Receipt,
    title: "AP / AR Management",
    desc: "Bills, invoices, and aging reports that protect your cash position. AR collections and subcontractor management available as add-ons if desired.",
    span: "md:col-span-1",
  },
  {
    Icon: Calendar,
    title: "HST / GST Filing Support",
    desc: "Tracking, calculations, and filing prep—never miss a CRA deadline.",
    span: "md:col-span-1",
  },
  {
    Icon: Users,
    title: "Payroll Coordination",
    desc: "Remittance tracking, provider liaison, and reconciled payroll—every pay run.",
    span: "md:col-span-1",
  },
  {
    Icon: Wrench,
    title: "Year-End Prep",
    desc: "Clean handoff packages for your accountant—corporations made effortless.",
    span: "md:col-span-1",
  },
];

const ADDONS = [
  {
    Icon: LineChart,
    title: "Cash Flow Forecasting",
    desc: "13-week rolling forecasts you can act on.",
  },
  {
    Icon: Wallet,
    title: "Budget Variance Analysis",
    desc: "Budget vs. actual reporting that drives accountability and smarter decisions.",
  },
];

const OTHER = [
  {
    Icon: ArrowRightLeft,
    title: "Software Migration",
    desc: "Seamless moves between QuickBooks, Xero, and other platforms—data intact, no downtime.",
  },
  {
    Icon: Landmark,
    title: "Tax Services",
    desc: "Personal tax returns and sole proprietorship filings—prepared and filed with the CRA.",
  },
  {
    Icon: Sparkles,
    title: "Paperless Transition & Workflow Automations",
    desc: "Move your business off paper—digital receipts, automated approvals, and clean cloud workflows.",
  },
  {
    Icon: Eraser,
    title: "Catch-Up & Clean-Up",
    desc: "Backlog recovery to current within weeks—then transitioned onto a clean monthly cadence.",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      className="section-anchor py-24 md:py-32 bg-cream"
      data-testid="services-section"
    >
      <div className="px-6 md:px-12 lg:px-20 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-end mb-16">
          <div className="md:col-span-7">
            <div className="uppercase text-xs tracking-[0.25em] font-bold text-gold mb-4">
              Services
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-navy tracking-tight leading-[1.05]">
              Premium bookkeeping, <span className="italic">precisely engineered</span> for
              Canadian businesses.
            </h2>
          </div>
          <p className="md:col-span-5 text-stone text-base md:text-lg leading-relaxed">
            From everyday reconciliations to year-end handoffs, every workflow is designed
            for compliance, clarity, and growth. Fixed monthly pricing keeps your costs
            predictable and includes all of these services tailored to fit your business needs.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 auto-rows-[1fr]">
          {CORE.map((s, i) => (
            <div
              key={s.title}
              data-testid={`service-card-${i}`}
              className={`${s.span} group p-8 bg-white border border-border hover:border-navy/30 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 flex flex-col`}
            >
              <div
                className={`inline-flex w-12 h-12 items-center justify-center mb-6 ${
                  s.accent ? "bg-navy text-cream" : "bg-accent text-navy"
                }`}
              >
                <s.Icon className="h-5 w-5" />
              </div>
              <h3 className="font-serif text-2xl text-navy mb-3 tracking-tight">
                {s.title}
              </h3>
              <p className="text-stone leading-relaxed text-[15px]">{s.desc}</p>
            </div>
          ))}
        </div>

        {/* Other Services */}
        <div className="mt-20">
          <div className="flex items-end justify-between gap-6 mb-8">
            <div>
              <div className="uppercase text-xs tracking-[0.25em] font-bold text-gold mb-3">
                Other Services
              </div>
              <h3 className="font-serif text-3xl md:text-4xl text-navy tracking-tight">
                Project work &amp; specialty offerings
              </h3>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
            {OTHER.map((o, i) => (
              <div
                key={o.title}
                data-testid={`other-card-${i}`}
                className="bg-cream p-8 hover:bg-white transition-colors"
              >
                <o.Icon className="h-5 w-5 text-gold mb-4" />
                <div className="font-serif text-xl text-navy mb-2">{o.title}</div>
                <div className="text-stone text-sm leading-relaxed">{o.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Add-ons */}
        <div className="mt-20">
          <div className="flex items-end justify-between gap-6 mb-8">
            <div>
              <div className="uppercase text-xs tracking-[0.25em] font-bold text-gold mb-3">
                Advanced Add-Ons
              </div>
              <h3 className="font-serif text-3xl md:text-4xl text-navy tracking-tight">
                Higher-leverage services for scaling operators
              </h3>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-px bg-border">
            {ADDONS.map((a, i) => (
              <div
                key={a.title}
                data-testid={`addon-card-${i}`}
                className="bg-cream p-8 hover:bg-white transition-colors"
              >
                <a.Icon className="h-5 w-5 text-gold mb-4" />
                <div className="font-serif text-xl text-navy mb-2">{a.title}</div>
                <div className="text-stone text-sm leading-relaxed">{a.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
